import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { SearchableSelect, type SearchableOption } from '@/components/form/SearchableSelect';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import { formatINR } from '@/lib/format';
import { listSisStores } from '@/api/sisStock';
import { previewSisCreditNote, generateSisCreditNote, generateGroupSisCreditNote, listSisCreditNoteLines } from '@/api/sisCreditNote';

const iso = (d: Date) => d.toISOString().slice(0, 10);

interface Props {
  open: boolean;
  onClose: () => void;
}

export function GenerateCreditNoteModal({ open, onClose }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const [mode, setMode] = useState<'store' | 'group'>('store');
  const [partyId, setPartyId] = useState('');
  const [group, setGroup] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');

  useEffect(() => {
    if (!open) return;
    const now = new Date();
    const from = new Date(now);
    from.setDate(from.getDate() - 15);
    setMode('store');
    setPartyId('');
    setGroup('');
    setStart(iso(from));
    setEnd(iso(now));
  }, [open]);

  const storesQ = useQuery({ queryKey: ['sis-stores'], queryFn: () => listSisStores(), enabled: open, staleTime: 0 });
  const storeOptions: SearchableOption[] = (storesQ.data ?? []).map((s) => ({ value: String(s.id), label: s.name, sublabel: s.city || undefined }));
  const groupOptions = useMemo(
    () => Array.from(new Set((storesQ.data ?? []).map((s) => s.group).filter(Boolean))).sort() as string[],
    [storesQ.data],
  );

  const rupee = (n: number) => `${n < 0 ? '−' : ''}₹${formatINR(Math.abs(n))}`;

  // ── Store-wise preview (detailed, existing) ──
  const storeReady = mode === 'store' && !!partyId && !!start && !!end;
  const previewQ = useQuery({
    queryKey: ['sis-cn-preview', partyId, start, end],
    queryFn: () => previewSisCreditNote(Number(partyId), start, end),
    enabled: open && storeReady,
  });
  const p = previewQ.data;
  const hasNotes = !!p && (p.debit.lines.length > 0 || p.credit.lines.length > 0);

  // ── Group-wise preview (per-store commission + difference summary) ──
  const groupReady = mode === 'group' && !!group && !!start && !!end;
  const groupLinesQ = useQuery({
    queryKey: ['sis-cn-grouplines', group, start, end],
    queryFn: () => listSisCreditNoteLines({ group, from: start, to: end }),
    enabled: open && groupReady,
  });
  const groupSummary = useMemo(() => {
    const m = new Map<string, { store: string; commission: number; difference: number }>();
    for (const l of groupLinesQ.data?.lines ?? []) {
      const row = m.get(l.storeName) ?? { store: l.storeName, commission: 0, difference: 0 };
      row.commission += l.commission;
      row.difference += l.priceDiff;
      m.set(l.storeName, row);
    }
    return [...m.values()].map((r) => ({ ...r, commission: Math.round(r.commission * 100) / 100, difference: Math.round(r.difference * 100) / 100 }));
  }, [groupLinesQ.data]);
  const groupHasNotes = groupSummary.length > 0;

  const gen = useMutation({
    mutationFn: () => generateSisCreditNote({ partyId: Number(partyId), periodStart: start, periodEnd: end }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['sis-credit-notes'] });
      const parts = [r.commission && `Commission ${r.commission.noteNo}`, r.difference && `Difference ${r.difference.noteNo}`].filter(Boolean);
      toast.push(parts.length ? `Generated ${parts.join(' + ')}` : 'Generated', 'success');
      onClose();
    },
    onError: (e) => toast.push(apiError(e, 'Could not generate note'), 'error'),
  });

  const genGroup = useMutation({
    mutationFn: () => generateGroupSisCreditNote({ group, periodStart: start, periodEnd: end }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['sis-credit-notes'] });
      const n = r.generated.length;
      toast.push(n ? `Generated notes for ${n} store${n === 1 ? '' : 's'} in ${r.group}${r.skipped.length ? ` (${r.skipped.length} skipped)` : ''}` : `Nothing to generate for ${r.group}`, n ? 'success' : 'info');
      onClose();
    },
    onError: (e) => toast.push(apiError(e, 'Could not generate group notes'), 'error'),
  });

  const busy = gen.isPending || genGroup.isPending;
  const canGenerate = mode === 'store' ? (hasNotes && !previewQ.isFetching) : (groupHasNotes && !groupLinesQ.isFetching);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Generate credit / debit note"
      size="xl"
      footer={
        <>
          <Button variant="glass" onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!canGenerate || busy} onClick={() => (mode === 'store' ? gen.mutate() : genGroup.mutate())}>
            {busy ? 'Generating…' : mode === 'store' ? 'Generate' : 'Generate for Group'}
          </Button>
        </>
      }
    >
      {/* Store-wise / Group-wise toggle */}
      <div className="mb-3 inline-flex rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] p-1 text-xs font-bold">
        {(['store', 'group'] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={`rounded-md px-3 py-1.5 transition ${mode === m ? 'bg-accent text-white' : 'text-[var(--text-secondary)] hover:text-accent'}`}
          >
            {m === 'store' ? 'Store-wise' : 'Group-wise'}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {mode === 'store' ? (
          <FormGroup label="Store" className="sm:col-span-3">
            <SearchableSelect options={storeOptions} value={partyId} onChange={(v) => setPartyId(v === '' ? '' : String(v))} placeholder="Type a store name…" />
          </FormGroup>
        ) : (
          <FormGroup label="Group" className="sm:col-span-3">
            <Select value={group} onChange={(e) => setGroup(e.target.value)}>
              <option value="">Select a group…</option>
              {groupOptions.map((g) => <option key={g}>{g}</option>)}
            </Select>
          </FormGroup>
        )}
        <FormGroup label="Period from"><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} /></FormGroup>
        <FormGroup label="Period to"><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} /></FormGroup>
      </div>

      {/* Store-wise detailed preview */}
      {storeReady && (
        <div className="mt-4 rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] p-4 backdrop-blur-xl">
          {previewQ.isFetching ? (
            <p className="text-sm text-[var(--text-muted)]">Calculating…</p>
          ) : !hasNotes ? (
            <p className="text-sm text-[var(--text-muted)]">Nothing to note in this period.</p>
          ) : (
            <>
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">{p!.storeName}</span>
                <span className="text-[10px] text-[var(--text-muted)]">commission {p!.commissionPct}%</span>
              </div>
              {([
                { title: 'Debit Note', tone: 'danger' as const, side: p!.debit },
                { title: 'Credit Note', tone: 'success' as const, side: p!.credit },
              ]).filter((s) => s.side.lines.length > 0).map((s) => (
                <div key={s.title} className="mb-3 last:mb-0">
                  <div className="mb-1 flex items-center justify-between">
                    <Badge tone={s.tone}>{s.title} · {rupee(s.side.amount)}</Badge>
                  </div>
                  <div className="overflow-x-auto">
                  <table className="w-full min-w-[560px] text-sm">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
                        <th className="py-1">Item</th>
                        <th className="py-1">Bill No</th>
                        <th className="py-1">Invoice</th>
                        <th className="py-1">Component</th>
                        <th className="py-1 text-right">Units</th>
                        <th className="py-1 text-right">Cost</th>
                        <th className="py-1 text-right">Sell</th>
                        <th className="py-1 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {s.side.lines.map((l, i) => (
                        <tr key={`${l.itemId}-${l.component}-${l.txnId ?? ''}-${l.invoiceNo ?? ''}-${i}`} className="border-t border-[var(--border-color)]/50">
                          <td className="py-1 whitespace-nowrap">{l.itemCode}</td>
                          <td className="py-1 whitespace-nowrap font-mono text-xs">{l.txnId ?? '—'}</td>
                          <td className="py-1 whitespace-nowrap font-mono text-xs">{l.invoiceNo ?? '—'}</td>
                          <td className="py-1 capitalize text-[var(--text-muted)]">{l.component === 'commission' ? 'Commission' : 'Price diff.'}</td>
                          <td className="py-1 text-right">{l.units}</td>
                          <td className="py-1 text-right text-[var(--text-muted)]">{l.costRate != null ? `₹${formatINR(l.costRate)}` : '—'}</td>
                          <td className="py-1 text-right text-[var(--text-muted)]">{l.sellRate != null ? `₹${formatINR(l.sellRate)}` : '—'}</td>
                          <td className="py-1 text-right font-medium">{rupee(l.variance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              ))}
              <p className="mt-2 text-xs text-[var(--text-muted)]">Two notes will be raised: one Commission, one Difference.</p>
            </>
          )}
        </div>
      )}

      {/* Group-wise per-store summary */}
      {groupReady && (
        <div className="mt-4 rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] p-4 backdrop-blur-xl">
          {groupLinesQ.isFetching ? (
            <p className="text-sm text-[var(--text-muted)]">Calculating…</p>
          ) : !groupHasNotes ? (
            <p className="text-sm text-[var(--text-muted)]">Nothing to note for any store in this group & period.</p>
          ) : (
            <>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">
                {group} — {groupSummary.length} store{groupSummary.length === 1 ? '' : 's'} · a Commission + Difference note each
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
                      <th className="py-1">Store</th>
                      <th className="py-1 text-right">Commission</th>
                      <th className="py-1 text-right">Difference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupSummary.map((r) => (
                      <tr key={r.store} className="border-t border-[var(--border-color)]/50">
                        <td className="py-1">{r.store}</td>
                        <td className="py-1 text-right font-medium">{rupee(r.commission)}</td>
                        <td className="py-1 text-right font-medium">{rupee(r.difference)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-2 text-xs text-[var(--text-muted)]">Stores already noted for this period are skipped.</p>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
